import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { showToast } from "./toastSlice";
import { hideLoader, showLoader } from "./loaderSlice";

const initialState = {
  list: [],
  currentSubscription: null,
  paymentMethods: null,
  billingInfo: null,
  loading: false,
  error: null,
  subscriptionId: null,
  runningCharges: null,
};

// Get all plans
export const getAllPlansAction = createAsyncThunk(
  "plans/getAll",
  async (_, { dispatch }) => {
    try {
      const res = await api("get", Constants.END_POINT.getPlans);
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to fetch plans"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

// Get current subscription
export const getCurrentSubscriptionAction = createAsyncThunk(
  "plans/getCurrentSubscription",
  async (orgId, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.getSubscription}/${orgId}`
      );
      if (res?.success && res?.data) {
        const subscription = res.data;
        return subscription;
      }
      return null;
    } catch (error) {
      console.error("Error fetching subscription:", error);
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Update subscription after successful Razorpay payment
export const updateSubscriptionAction = createAsyncThunk(
  "plans/updateSubscription",
  async ({ orgId, planId }, { dispatch }) => {
    try {
      const res = await api("patch", Constants.END_POINT.upgradeSubscription, {
        orgId,
        planId,
      });

      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Subscription updated successfully",
          })
        );
        return res.data;
      } else {
        throw new Error(res?.data?.message || "Failed to update subscription");
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

export const cancelSubscriptionUpdate = createAsyncThunk(
  "plans/cancelUpgrade",
  async ({ orgId, subId }, { dispatch }) => {
    try {
      const res = await api(
        "delete",
        `${Constants.END_POINT.cancelUpgrade}/${orgId}/${subId}`
      );

      if (res?.success) {
        // dispatch(
        //   showToast({
        //     severity: "success",
        //     summary: "Subscription updated successfully",
        //   })
        // );
        return res.data;
      } else {
        throw new Error(res?.data?.message || "Failed to update subscription");
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      // dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);
// Pay pending invoice
export const payNowAction = createAsyncThunk(
  "plans/payNow",
  async (_, { dispatch }) => {
    try {
      const res = await api("post", Constants.END_POINT.payNow);
      if (res?.success) {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          order_id: res.data.orderId,
          handler: async function (response) {
            if (response.razorpay_payment_id) {
              try {
                await dispatch(
                  handlePayNowPayment(response.razorpay_payment_id)
                ).unwrap();
                dispatch(
                  showToast({
                    severity: "success",
                    summary: "Payment completed successfully.",
                    detail: "Payment completed successfully",
                  })
                );
              } catch (error) {
                console.error("Error handling payment:", error);
              }
            }
          },
          theme: {
            color: "#4B5563",
          },
          method: {
            card: true,
            netbanking: false,
            wallet: false,
            upi: false,
            paypal: false,
            emi: false,
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to process payment"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

// Handle successful payment
export const handlePayNowPayment = createAsyncThunk(
  "plans/handlePayNowPayment",
  async (paymentId, { dispatch }) => {
    try {
      const res = await api("post", Constants.END_POINT.handlePayNow, {
        paymentId,
      });
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to process payment"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Charge card for payment method verification
export const chargeCardAction = createAsyncThunk(
  "plans/chargeCard",
  async (data, { dispatch }) => {
    try {
      const res = await api(
        "post",
        Constants.END_POINT.chargeCard + data?.orgId
      );
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to process card charge"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

// Handle card verification
export const handleCardVerification = createAsyncThunk(
  "plans/handleCardVerification",
  async ({ orderId, paymentId }, { dispatch }) => {
    try {
      const res = await api("post", Constants.END_POINT.verifyCard, {
        orderId,
        paymentId,
      });
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to verify card"
        );
      }
    } catch (error) {
      console.error("Error in handleCardVerification:", error);
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Get payment methods (card details) from Razorpay
export const getPaymentMethodsAction = createAsyncThunk(
  "plans/getPaymentMethods",
  async (paymentId, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.getPaymentMethods}/${paymentId}`
      );
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message ||
            res?.message ||
            "Failed to fetch payment methods"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Buy subscription
export const buySubscriptionAction = createAsyncThunk(
  "plans/buySubscription",
  async (data, { dispatch }) => {
    try {
      const res = await api("post", Constants.END_POINT.buySubscription, data);
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: res?.message,
          })
        );

        dispatch(
          planSlice.actions.setSubscription(
            res?.data?.subscriptionId || res?.data?.previouSubscription
          )
        );
        if (res?.data?.previouSubscription) {
          dispatch(
            showToast({
              severity: "success",
              summary: res?.message,
            })
          );
        }
        return res.data;
      } else {
        throw new Error(
          res?.data?.message ||
            res?.message ||
            "Failed to purchase subscription"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

// Update billing information
export const updateBillingInfoAction = createAsyncThunk(
  "plans/updateBillingInfo",
  async ({ orgId, data }, { dispatch }) => {
    try {
      const res = await api("post", Constants.END_POINT.addBillingInfo, {
        org_id: orgId,
        ...data,
      });
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Billing information updated successfully",
          })
        );
        return res.data;
      } else {
        throw new Error(
          res?.data?.message ||
            res?.message ||
            "Failed to update billing information"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

// Get invoices
export const getInvoicesAction = createAsyncThunk(
  "plans/getInvoices",
  async ({ orgId, pageNumber, pageSize }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.getInvoices}${orgId}?pageSize=${pageSize}&pageNumber=${pageNumber}`
      );
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to fetch invoices"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

export const getInvoiceDetailsActions = createAsyncThunk(
  "plans/getInvoiceDetails",
  async ({ orgId, invoiceId }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.downloadInvoice}/${orgId}/${invoiceId}`
      );
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to fetch invoices"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Get running charges from BigQuery
// Get running charges from BigQuery
export const getRunningChargesAction = createAsyncThunk(
  "plans/getRunningCharges",
  async (params, { dispatch }) => {
    try {
      let orgId, startDate, endDate;

      if (typeof params === "string" || typeof params === "number") {
        orgId = params;
      } else {
        orgId = params.orgId;
        startDate = params.startDate;
        endDate = params.endDate;
      }

      let url = `${Constants.END_POINT.getRunningCharges}${orgId}`;
      if (startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      }

      const res = await api("get", url);
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message ||
            res?.message ||
            "Failed to fetch running charges"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

export const getUsageDetails = createAsyncThunk(
  "plans/getUsageDetails",
  async ({ orgId }, { dispatch }) => {
    try {
      const res = await api("get", `${Constants.END_POINT.getUsage}/${orgId}`);
      if (res?.success) {
        return res.data;
      } else {
        throw new Error(
          res?.data?.message || res?.message || "Failed to fetch invoices"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

const planSlice = createSlice({
  name: "plans",
  initialState,
  reducers: {
    setSubscription: (state, action) => {
      state.subscriptionId = action.payload;
    },
    setSubscribedPlan: (state, action) => {
      state.currentSubscription = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Get all plans
      .addCase(getAllPlansAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPlansAction.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.error = null;
      })
      .addCase(getAllPlansAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get current subscription
      .addCase(getCurrentSubscriptionAction.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentSubscription = action.payload;
        } else {
          state.currentSubscription = null;
        }
      })
      // Get payment methods
      .addCase(getPaymentMethodsAction.fulfilled, (state, action) => {
        state.paymentMethods = action.payload;
        // Update billing info with new card details
        if (state.billingInfo && action.payload) {
          state.billingInfo = {
            ...state.billingInfo,
            card_details: action.payload,
          };
        }
      })
      .addCase(cancelSubscriptionUpdate.fulfilled, (state, action) => {
        state.currentSubscription.changes = null;
      })
      // Get running charges
      .addCase(getRunningChargesAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRunningChargesAction.fulfilled, (state, action) => {
        state.loading = false;
        state.runningCharges = action.payload;
        state.error = null;
      })
      .addCase(getRunningChargesAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});
export const { setSubscribedPlan } = planSlice.actions;
export default planSlice.reducer;
